import { useCallback, useSyncExternalStore } from 'react'
import type { ColorScheme } from '../theme/palette'

/**
 * The device preferences the whole app honours, read one way.
 *
 * Before integration there were two of these: the map subscribed with
 * `useSyncExternalStore` and the filter panel with `useState` plus an effect,
 * which meant the two disagreed for one frame after every change and the map
 * could paint a light basemap behind a dark panel. One hook, one subscription
 * model.
 *
 * `useSyncExternalStore` rather than `useState` + an effect, so the first render
 * already has the right answer and there is no flash of the light basemap for
 * somebody in dark mode.
 */

const NOOP_UNSUBSCRIBE = (): void => undefined

const matchMediaSafe = (query: string): MediaQueryList | null =>
  typeof window === 'undefined' || typeof window.matchMedia !== 'function'
    ? null
    : window.matchMedia(query)

export const useMediaQuery = (query: string): boolean => {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = matchMediaSafe(query)
      if (list === null) return NOOP_UNSUBSCRIBE
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    },
    [query],
  )

  const getSnapshot = useCallback(() => matchMediaSafe(query)?.matches ?? false, [query])

  // The server, and jsdom, have no media queries; everything defaults to "no",
  // which is the desktop, light, full-motion layout.
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/**
 * The one breakpoint the layout changes shape at.
 *
 * Above it: three columns, the rail and the panel always present. Below it: the
 * map takes the screen and both panels become sheets in thumb reach. It is a
 * constant rather than a magic string in two files because `styles.css` has the
 * matching `@media (max-width: 60rem)` and the two must not drift.
 */
export const COMPACT_QUERY = '(max-width: 60rem)'

export const useIsCompact = (): boolean => useMediaQuery(COMPACT_QUERY)

export const useColorScheme = (): ColorScheme =>
  useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light'

export const usePrefersReducedMotion = (): boolean =>
  useMediaQuery('(prefers-reduced-motion: reduce)')
